// Steering targets (movement.md §4.2–4.3): the touch joystick as a heading with proportional
// speed, follow-finger, the release rules and the pre-first-touch rest, plus `SteerController`,
// which picks the active device each display frame and drives the InputSender.
//
// Own-motion prediction (DEVIATION from movement.md §4.2's "no forward prediction", owner
// review 2026-10-24): the joystick and the stop-on-release target are placed relative to `P`,
// where the server cell will be when the tick that applies an INPUT sent now starts, instead of
// relative to `ownNewest`. `predictOwn` replays the server's own steering law (shared
// `steerVelocity`, same K, β, vmax) from the newest FRAME's position and velocity through the
// ticks still to run before that one, feeding each tick the INPUT this client sent that the
// tick will have seen (`InputSender.sentLog`; see `predictOwn` for the timing). With
// `ownNewest + reach(s, L)` the old formula assumed the cell keeps its newest speed for the loop delay;
// when the cell slowed down (stick eased, finger lifted with "Stop when finger lifts" on) the
// target landed behind the server cell at mobile RTTs and the cell reversed (review: −0.28 vmax
// at 60 ms one way, −0.64 at 100 ms). The replay is not a guess that can feed back on itself:
// it is the server's law applied to inputs whose effect is already decided. Modelled at one way
// 0–150 ms (±10 ms jitter, ±30 ms RTT error): no stop reverses by more than 0.09·vmax.
//
// Joystick (`stick` = the raw vector from touch.ts, |stick| ≤ 1 at 60 px):
// - `s = clamp((|stick| − 0.10) / 0.80, 0, 1)`: a 6 px dead zone, full speed at 54 px.
// - Deflection hold: a drop in `s` while the finger is down applies only once it has lasted
//   `STICK_HOLD_MS` (50 ms; rises apply at once), so a knob springing back as the thumb lifts
//   never reaches the server (the same 50 ms rule touch.ts uses for `releaseStick`).
// - `ahead(s) = s < 1 ? REACH_GAIN·s·vmax/K : 4·vmax/K`, `vmax = speedOf(rMax)`, K the server's
//   steering gain: the server's zone gives `v = K·ahead = s·vmax`; the second form saturates.
// - While held: `target = P + dir·ahead(s)`, recomputed every frame (never frozen in world space,
//   never from the viewport width).
// - After the lift, "Stop when finger lifts" off (default): `target = P + lastDir·ahead(lastS)`
//   every frame, so the cell keeps its heading and speed until the next touch (touch.ts keeps
//   the strongest vector of the last 50 ms as `releaseStick`). On: `target = P` every frame: the
//   server's velocity easing brings the cell to rest (90 → 10 % in ~160 ms) with no reversal.
// - Before the first touch of a life: `target = ownNewest` (at rest).
// Follow finger: `target = ownNewest + (finger − screen centre)/zoom` while the finger is down;
// after the lift the last finger position is kept (stop off) or the target is `P` (stop on).
// Spectating (no own cells): the camera centre is the anchor and the reach is `s` × the smaller
// visible half-extent.
//
// Implementer: client input. Consumers: app.ts (one SteerController per page, `frame` once per
// display frame instead of calling InputSender.update directly). DOM-free.

import { clamp, SIM_DEFAULTS, speedOf, steerBeta, steerVelocity, type Vec2 } from '@ballio/shared';
import type { Camera } from '../game/camera.js';
import type { TouchSteerMode } from '../settings/store.js';
import type { InputSender, SentLog } from './input-sender.js';

/** Stick dead zone, as a fraction of the joystick radius (6 px of 60). */
export const STICK_DEAD_ZONE = 0.1;
/** Deflection for full speed, as a fraction of the radius (54 px of 60). */
export const STICK_FULL = 0.9;
/**
 * Proportional reach gain and the saturating multiple at s = 1. With the prediction the steady
 * speed is `REACH_GAIN·s·vmax`; 1.02 leaves a little for the server ticks the replay cannot
 * see (review: 1.1 without prediction read 6–10 % high).
 */
export const REACH_GAIN = 1.02;
export const REACH_SATURATE = 4;
/** A drop in stick deflection applies only after it has lasted this long (ms). */
export const STICK_HOLD_MS = 50;
/** At most this many server ticks are replayed (0.48 s at 25 Hz; L's clamp is 0.30 s). */
export const PREDICT_MAX_TICKS = 12;

/** Speed fraction for a stick of length `len` (0 inside the dead zone, 1 from 90 %). */
export function stickDeflection(len: number): number {
  if (!(len > 0) || !Number.isFinite(len)) return 0;
  return clamp((len - STICK_DEAD_ZONE) / (STICK_FULL - STICK_DEAD_ZONE), 0, 1);
}

/**
 * World distance ahead of the predicted own position for deflection `s` (movement.md §4.3):
 * `s < 1 ? REACH_GAIN·s·vmax/K : 4·vmax/K`; 0 for s ≤ 0. The server's proportional zone then
 * drives the cell at `K·ahead = s·vmax`.
 */
export function joystickReach(
  s: number,
  vmax: number,
  gain: number = SIM_DEFAULTS.steer.gain,
): number {
  if (!(s > 0) || !(vmax > 0) || !(gain > 0)) return 0;
  return s < 1 ? (REACH_GAIN * s * vmax) / gain : (REACH_SATURATE * vmax) / gain;
}

/** What `predictOwn` replays from. */
export interface PredictArgs {
  /** The newest FRAME's own centroid, velocity (u/s) and largest radius. */
  readonly own: Readonly<OwnAnchor>;
  readonly nowMs: number;
  /** Local time at which the FRAME of `own`'s tick arrived (or would have). */
  readonly ownTickAtMs: number;
  /** Local ms per server tick (`clock.dtEstMs`). */
  readonly tickMs: number;
  /** Server seconds per tick (1 / tickRate). */
  readonly tickSec: number;
  /** Smoothed RTT (ms); ≤ 0 = unknown, treated as 0. */
  readonly rttMs: number;
  /** INPUTs sent lately. */
  readonly sent: SentLog;
}

const predictTarget: Vec2 = { x: 0, y: 0 };
const predictSv: Vec2 = { x: 0, y: 0 };

/**
 * Where the server cell will be when the tick that applies an INPUT sent now starts
 * (movement.md §4.3, header): from `own` (position after tick n, steering velocity = its
 * newest-sample velocity, capped at vmax), replay ticks n+1, n+2, … while their FRAME would
 * arrive before `now + rtt` (a tick that runs `down` ms before its FRAME arrives sees an INPUT
 * sent at `s` when `s + up ≤ arrival − down`, i.e. `s ≤ arrival − rtt`). Each replayed tick
 * uses the newest logged INPUT sent at or before `arrival − rtt`; with none logged the velocity
 * is kept. At most PREDICT_MAX_TICKS ticks. Writes into and returns `out`.
 */
export function predictOwn(a: PredictArgs, out: Vec2): Vec2 {
  const own = a.own;
  let x = own.x;
  let y = own.y;
  out.x = x;
  out.y = y;
  const tickMs = a.tickMs > 0 && Number.isFinite(a.tickMs) ? a.tickMs : 40;
  const dt = a.tickSec > 0 && Number.isFinite(a.tickSec) ? a.tickSec : 0.04;
  const rtt = a.rttMs > 0 && Number.isFinite(a.rttMs) ? a.rttMs : 0;
  if (!Number.isFinite(a.ownTickAtMs) || !Number.isFinite(a.nowMs)) return out;
  const vmax = speedOf(own.rMax > 0 ? own.rMax : 1);
  const gain = SIM_DEFAULTS.steer.gain;
  const beta = steerBeta(dt, SIM_DEFAULTS.steer.velTauSec);
  const sv = predictSv;
  sv.x = Number.isFinite(own.vx) ? (own.vx as number) : 0;
  sv.y = Number.isFinite(own.vy) ? (own.vy as number) : 0;
  const v = Math.hypot(sv.x, sv.y);
  // A split's boost shows in the sample velocity; the steering velocity never exceeds vmax.
  if (v > vmax) {
    sv.x *= vmax / v;
    sv.y *= vmax / v;
  }
  const tg = predictTarget;
  const until = a.nowMs + rtt;
  for (let j = 1; j <= PREDICT_MAX_TICKS; j++) {
    const arrival = a.ownTickAtMs + j * tickMs;
    if (arrival >= until) break;
    if (a.sent.latestAtOrBefore(arrival - rtt, tg)) {
      const dx = tg.x - x;
      const dy = tg.y - y;
      steerVelocity(dx, dy, Math.sqrt(dx * dx + dy * dy), vmax, gain, beta, sv);
    }
    x += sv.x * dt;
    y += sv.y * dt;
  }
  out.x = x;
  out.y = y;
  return out;
}

/** The own-cell anchor (store.ownNewest(): r²-weighted centroid and largest radius, newest FRAME). */
export interface OwnAnchor {
  readonly x: number;
  readonly y: number;
  readonly rMax: number;
  /** Velocity (u/s) from the two newest samples; missing = 0. */
  readonly vx?: number;
  readonly vy?: number;
  /** Newest sample tick; missing = the newest FRAME's. */
  readonly tick?: number;
}

/** What the controller reads from the touch layer (TouchInput implements it). */
export interface TouchSteerSource {
  /** Touch is in use on this device. */
  readonly enabled: boolean;
  readonly mode: TouchSteerMode;
  /** Live joystick vector (|v| ≤ 1), null when no steering touch is down. */
  readonly stick: Readonly<Vec2> | null;
  /** Live finger position (CSS px), null when no steering touch is down. */
  readonly finger: Readonly<Vec2> | null;
  /** The vector to keep after the last lift (strongest of its last 50 ms); null = stop. */
  readonly releaseStick: Readonly<Vec2> | null;
  /** The finger position at the last lift; null = stop. */
  readonly releaseFinger: Readonly<Vec2> | null;
  /** Steering touch-downs so far (changes on every new steering touch). */
  readonly steerSeq: number;
}

/** The mouse pointer (MouseInput implements it). */
export interface PointerSource {
  readonly x: number;
  readonly y: number;
  readonly hasPointer: boolean;
}

/** An analog stick that already applied its dead zone (GamepadInput implements it). */
export interface StickSource {
  readonly stick: Readonly<Vec2> | null;
}

/** Per-frame facts from the store, socket, clock and settings. */
export interface SteerFrameInfo {
  /** `store.ownNewest()`; null while not playing. */
  readonly own: Readonly<OwnAnchor> | null;
  /** Smoothed RTT (`socket.rttMs`; 0 = unknown). */
  readonly rttMs: number;
  /** Local time of the newest FRAME (`clock.lastFrameAtMs`). */
  readonly lastFrameAtMs: number;
  /** Local ms per server tick (`clock.dtEstMs`); 40 when missing. */
  readonly tickMs?: number;
  /** Tick of the newest FRAME (`clock.lastTick`); missing = `own.tick`. */
  readonly lastTick?: number;
  /** Server seconds per tick (`store.tickMs / 1000`); 0.04 when missing. */
  readonly tickSec?: number;
  /** "Stop when finger lifts" (`settings.touchStopOnRelease`). */
  readonly stopOnRelease: boolean;
}

/** Where the joystick or a finger puts the target, given everything it depends on. */
export interface StickTargetArgs {
  /** Own centroid (its rMax sets vmax), or null when spectating (the camera centre anchors). */
  readonly own: Readonly<OwnAnchor> | null;
  readonly camera: Camera;
  /** Raw stick vector (|v| ≤ 1); null or zero-length = rest at the anchor. */
  readonly stick: Readonly<Vec2> | null;
  /** The predicted own position (`predictOwn`); missing = the own centroid. */
  readonly anchor?: Readonly<Vec2> | null;
}

/** Joystick target: `anchor + dir·ahead(s)` (movement.md §4.3). */
export function stickTarget(a: StickTargetArgs, out: Vec2): Vec2 {
  const p = a.own ? (a.anchor ?? a.own) : null;
  const ax = p ? p.x : a.camera.x;
  const ay = p ? p.y : a.camera.y;
  out.x = ax;
  out.y = ay;
  const v = a.stick;
  if (!v) return out;
  const len = Math.hypot(v.x, v.y);
  const s = stickDeflection(len);
  if (!(s > 0)) return out;
  let reach: number;
  if (a.own) {
    reach = joystickReach(s, speedOf(a.own.rMax > 0 ? a.own.rMax : 1));
  } else {
    const z = a.camera.zoom > 0 ? a.camera.zoom : 1;
    reach = (s * Math.min(a.camera.width, a.camera.height)) / 2 / z;
  }
  out.x = ax + (v.x / len) * reach;
  out.y = ay + (v.y / len) * reach;
  return out;
}

/** Follow-finger target: `anchor + (finger − screen centre)/zoom`; the anchor when `finger` is null. */
export function fingerTarget(
  own: Readonly<OwnAnchor> | null,
  camera: Camera,
  finger: Readonly<Vec2> | null,
  out: Vec2,
): Vec2 {
  const ax = own ? own.x : camera.x;
  const ay = own ? own.y : camera.y;
  out.x = ax;
  out.y = ay;
  if (!finger) return out;
  const z = camera.zoom > 0 ? camera.zoom : 1;
  out.x += (finger.x - camera.width / 2) / z;
  out.y += (finger.y - camera.height / 2) / z;
  return out;
}

type Device = 'mouse' | 'touch' | 'pad';

/** A pointer move larger than this (CSS px) hands steering back to the mouse. */
const POINTER_MOVE_PX = 1;

/**
 * Picks the steering device each frame and feeds the InputSender:
 * - Mouse: the pointer, through the sender's camera/server anchor (§4.2). Before the pointer
 *   has ever moved, the target is the own centroid (no chase of the canvas centre).
 * - Touch (once a touch happened, or on a touch device without a mouse): the joystick or
 *   follow-finger rules above, recomputed every frame.
 * - Gamepad: the left stick as a joystick; a centred stick rests at the own centroid until the
 *   mouse moves or a touch begins.
 * The device last used wins.
 */
export class SteerController {
  private readonly input: InputSender;
  private readonly mouse: PointerSource;
  private readonly touch: TouchSteerSource | null;
  private readonly pad: StickSource | null;
  private device: Device | null = null;
  private lastPx = Number.NaN;
  private lastPy = Number.NaN;
  private seenSeq = 0;
  private alive = false;
  /** A steering touch went down during this life (release rules apply only then). */
  private lifeTouched = false;
  private readonly out: Vec2 = { x: 0, y: 0 };
  private readonly padScratch: Vec2 = { x: 0, y: 0 };
  private readonly predicted: Vec2 = { x: 0, y: 0 };
  private readonly held: Vec2 = { x: 0, y: 0 };
  /** Stick deflections seen per frame during the current steering touch (the hold window). */
  private readonly holdT: number[] = [];
  private readonly holdS: number[] = [];
  private readonly holdX: number[] = [];
  private readonly holdY: number[] = [];
  private holdSeq = -1;
  private readonly ctx: {
    ownNewest: Readonly<OwnAnchor> | null;
    rttMs: number;
    target: Vec2 | null;
  } = { ownNewest: null, rttMs: 0, target: null };

  constructor(
    input: InputSender,
    mouse: PointerSource,
    touch: TouchSteerSource | null = null,
    pad: StickSource | null = null,
  ) {
    this.input = input;
    this.mouse = mouse;
    this.touch = touch;
    this.pad = pad;
    this.seenSeq = touch?.steerSeq ?? 0;
  }

  /** The device steering now (null before the first frame). */
  get activeDevice(): Device | null {
    return this.device;
  }

  /** One display frame: compute the target and run `input.update`. */
  frame(nowMs: number, camera: Camera, info: SteerFrameInfo): void {
    const own = info.own;
    const touch = this.touch;
    this.trackLife(own !== null, touch);
    const device = this.pickDevice(touch);
    this.device = device;
    const ctx = this.ctx;
    ctx.ownNewest = own;
    ctx.rttMs = info.rttMs;
    ctx.target = null;
    let px = Number.NaN;
    let py = Number.NaN;
    const anchor = own ? this.predict(nowMs, own, info) : null;

    if (device === 'pad') {
      ctx.target = this.padTarget(own, camera, this.pad?.stick ?? null, anchor);
    } else if (device === 'touch' && touch) {
      ctx.target = this.touchTarget(nowMs, touch, own, camera, anchor, info.stopOnRelease);
    } else if (this.mouse.hasPointer) {
      px = this.mouse.x;
      py = this.mouse.y;
    } else if (own) {
      // No pointer yet: rest at the own centroid.
      this.out.x = own.x;
      this.out.y = own.y;
      ctx.target = this.out;
    } else {
      px = this.mouse.x;
      py = this.mouse.y;
    }
    this.input.update(nowMs, camera, px, py, ctx);
  }

  // -------------------------------------------------------------------------------------------

  /** `predictOwn` from this frame's facts. */
  private predict(nowMs: number, own: Readonly<OwnAnchor>, info: SteerFrameInfo): Vec2 {
    const tickMs = info.tickMs !== undefined && info.tickMs > 0 ? info.tickMs : 40;
    const lastTick = info.lastTick ?? own.tick;
    const ownTick = own.tick ?? lastTick;
    // A cell missing from the newer FRAMEs did not move in them (MOVE only when it moved).
    const stale =
      lastTick !== undefined && ownTick !== undefined && Number.isFinite(lastTick)
        ? Math.max(0, lastTick - ownTick)
        : 0;
    const base: OwnAnchor = stale > 0 ? { x: own.x, y: own.y, rMax: own.rMax, vx: 0, vy: 0 } : own;
    return predictOwn(
      {
        own: base,
        nowMs,
        ownTickAtMs: info.lastFrameAtMs - stale * tickMs,
        tickMs,
        tickSec: info.tickSec ?? 0.04,
        rttMs: info.rttMs,
        sent: this.input.sentLog,
      },
      this.predicted,
    );
  }

  private trackLife(alive: boolean, touch: TouchSteerSource | null): void {
    if (alive && !this.alive) {
      // A touch already down when the life starts counts; earlier lifts do not.
      this.lifeTouched = touch !== null && (touch.stick !== null || touch.finger !== null);
    }
    this.alive = alive;
  }

  private pickDevice(touch: TouchSteerSource | null): Device {
    const m = this.mouse;
    if (touch && touch.steerSeq !== this.seenSeq) {
      this.seenSeq = touch.steerSeq;
      this.lifeTouched = true;
      this.device = 'touch';
    }
    if (touch && (touch.stick !== null || touch.finger !== null)) this.device = 'touch';
    const pad = this.pad?.stick ?? null;
    if (pad) this.device = 'pad';
    if (m.hasPointer) {
      const first = Number.isNaN(this.lastPx);
      if (
        first ||
        Math.abs(m.x - this.lastPx) > POINTER_MOVE_PX ||
        Math.abs(m.y - this.lastPy) > POINTER_MOVE_PX
      ) {
        this.lastPx = m.x;
        this.lastPy = m.y;
        // A mouse move takes over unless a touch or the pad is steering right now.
        const live = (touch !== null && (touch.stick !== null || touch.finger !== null)) || pad;
        if (!live) this.device = 'mouse';
      }
    }
    if (this.device === null) this.device = touch && touch.enabled ? 'touch' : 'mouse';
    return this.device;
  }

  private touchTarget(
    nowMs: number,
    touch: TouchSteerSource,
    own: Readonly<OwnAnchor> | null,
    camera: Camera,
    anchor: Readonly<Vec2> | null,
    stopOnRelease: boolean,
  ): Vec2 {
    const coast = this.lifeTouched && !stopOnRelease;
    const down = touch.stick !== null || touch.finger !== null;
    if (touch.mode === 'follow') {
      if (!down && !coast && own && anchor && this.lifeTouched) {
        // Stop on release: rest where the server cell will be (no pull back).
        this.out.x = anchor.x;
        this.out.y = anchor.y;
        return this.out;
      }
      const finger = touch.finger ?? (coast ? touch.releaseFinger : null);
      return fingerTarget(own, camera, finger, this.out);
    }
    let stick: Readonly<Vec2> | null;
    if (touch.stick) {
      stick = this.holdStick(nowMs, touch.steerSeq, touch.stick);
    } else {
      this.holdSeq = -1;
      stick = coast ? touch.releaseStick : null;
    }
    // Before the first touch of a life the cell rests at ownNewest itself.
    const at = this.lifeTouched ? anchor : null;
    return stickTarget({ own, camera, stick, anchor: at }, this.out);
  }

  /**
   * The stick with drops in deflection delayed by STICK_HOLD_MS (header): the largest deflection
   * seen in the window, along the live direction (or the strongest sample's inside the dead
   * zone).
   */
  private holdStick(nowMs: number, seq: number, raw: Readonly<Vec2>): Readonly<Vec2> {
    const T = this.holdT;
    const S = this.holdS;
    const X = this.holdX;
    const Y = this.holdY;
    if (seq !== this.holdSeq) {
      this.holdSeq = seq;
      T.length = S.length = X.length = Y.length = 0;
    }
    const len = Math.hypot(raw.x, raw.y);
    const sNow = stickDeflection(len);
    T.push(nowMs);
    S.push(sNow);
    X.push(raw.x);
    Y.push(raw.y);
    // Keep the newest sample that is at least STICK_HOLD_MS old (it was in effect at the window
    // start) and everything after it.
    let drop = 0;
    while (drop + 1 < T.length && nowMs - (T[drop + 1] as number) >= STICK_HOLD_MS) drop++;
    if (drop > 0) {
      T.splice(0, drop);
      S.splice(0, drop);
      X.splice(0, drop);
      Y.splice(0, drop);
    }
    let best = 0;
    for (let i = 1; i < S.length; i++) if ((S[i] as number) > (S[best] as number)) best = i;
    const sMax = S[best] as number;
    if (!(sMax > sNow)) return raw;
    let dx = raw.x;
    let dy = raw.y;
    let dl = len;
    if (!(len >= STICK_DEAD_ZONE)) {
      dx = X[best] as number;
      dy = Y[best] as number;
      dl = Math.hypot(dx, dy);
    }
    if (!(dl > 0)) return raw;
    const k = (STICK_DEAD_ZONE + sMax * (STICK_FULL - STICK_DEAD_ZONE)) / dl;
    this.held.x = dx * k;
    this.held.y = dy * k;
    return this.held;
  }

  private padTarget(
    own: Readonly<OwnAnchor> | null,
    camera: Camera,
    stick: Readonly<Vec2> | null,
    anchor: Readonly<Vec2> | null,
  ): Vec2 {
    // A centred stick rests where the server cell will stop (no pull back).
    if (!stick) return stickTarget({ own, camera, stick: null, anchor }, this.out);
    // Map the pad's [0, 1] length onto the touch scale so `stickDeflection` returns it unchanged.
    const len = Math.hypot(stick.x, stick.y);
    if (!(len > 0)) return stickTarget({ own, camera, stick: null, anchor }, this.out);
    const raw = STICK_DEAD_ZONE + Math.min(1, len) * (STICK_FULL - STICK_DEAD_ZONE);
    const k = raw / len;
    this.padScratch.x = stick.x * k;
    this.padScratch.y = stick.y * k;
    return stickTarget({ own, camera, stick: this.padScratch, anchor }, this.out);
  }
}
